/**
 * Color scale for the engagement heatmap.
 *
 * Maps engagement_score (0..1) to a Tailwind background + text pair. Anchored
 * to the app's dark-theme palette: slate-800/700 for low engagement, emerald
 * 700→400 as the score climbs. Pure function; no component dependencies so
 * it is trivially testable and shareable between the grid cells and the
 * legend.
 */

export interface CellColor {
  bg: string;
  text: string;
}

/**
 * Return the background+text classes for a given engagement score.
 *
 * Scores outside [0, 1] (NaN, -Infinity, etc.) map to a neutral gray.
 */
export function engagementColor(score: number): CellColor {
  if (!Number.isFinite(score) || score < 0) {
    return { bg: 'bg-slate-800/50', text: 'text-slate-400' };
  }
  if (score >= 0.9) return { bg: 'bg-emerald-400', text: 'text-slate-900' };
  if (score >= 0.75) return { bg: 'bg-emerald-500', text: 'text-white' };
  if (score >= 0.5) return { bg: 'bg-emerald-600/80', text: 'text-white' };
  if (score >= 0.25) return { bg: 'bg-emerald-700/60', text: 'text-slate-200' };
  if (score > 0) return { bg: 'bg-slate-700/50', text: 'text-slate-300' };
  return { bg: 'bg-slate-800/40', text: 'text-slate-400' };
}

export interface LegendBucket {
  /** Inclusive lower bound of the bucket in percentage points. */
  lo: number;
  /** Inclusive upper bound of the bucket in percentage points. */
  hi: number;
  label: string;
  color: string;
}

export const LEGEND_BUCKETS: LegendBucket[] = [
  { lo: 0, hi: 0, label: '0', color: 'bg-slate-800/40' },
  { lo: 1, hi: 24, label: '1–24', color: 'bg-slate-700/50' },
  { lo: 25, hi: 49, label: '25–49', color: 'bg-emerald-700/60' },
  { lo: 50, hi: 74, label: '50–74', color: 'bg-emerald-600/80' },
  { lo: 75, hi: 89, label: '75–89', color: 'bg-emerald-500' },
  { lo: 90, hi: 100, label: '90–100', color: 'bg-emerald-400' },
];
